import type {
  BannerPageRule,
  DashboardBanner,
} from "@/content/dashboardBanners";
import {
  type InlineBannerSlot,
  OVERLAY_TEMPLATES,
  type OverlayTemplate,
} from "@/content/dashboardBannerOptions";
import { DEFAULT_CUSTOMIZE_PATHNAME } from "@/utils/formHref";

/** Checkout and the pages after a purchase never show a banner. */
const BANNER_FREE_PAGES: BannerPageRule[] = [
  DEFAULT_CUSTOMIZE_PATHNAME,
  "/subscription/pending",
].map((path) => ({ match: "startsWith", path }));

/**
 * Where a placement draws: a template, for Inline the slot it fills, or the
 * overlay, the one place over the page that a Pop-up or a Slide-in floats in.
 */
export type BannerPlace =
  | { template: "topBar" }
  | { template: "stickyBottomBar" }
  | { template: "inline"; slot: InlineBannerSlot }
  | { template: "overlay" };

type TemplateOf<P extends BannerPlace> = P extends { template: "overlay" }
  ? OverlayTemplate
  : P["template"];

export type BannerOf<P extends BannerPlace> = Extract<
  DashboardBanner,
  { template: TemplateOf<P> }
>;

/** A Pop-up or a Slide-in: it floats over the page and opens on a trigger. */
export const isOverlay = (
  banner: DashboardBanner
): banner is BannerOf<{ template: "overlay" }> =>
  (OVERLAY_TEMPLATES as ReadonlyArray<string>).includes(banner.template);

/**
 * The banners for one place that may show on this page, in Sanity's order. A
 * floating banner set to Off is paused: it is left out here, before any flag
 * is read, so it records no exposure and leaves the overlay to the next one.
 */
export const bannersForPage = <P extends BannerPlace>(
  banners: DashboardBanner[],
  place: P,
  pathname: string
): BannerOf<P>[] => {
  const page = withoutPlanId(pathname);
  if (BANNER_FREE_PAGES.some((rule) => matches(page, rule))) return [];
  return banners.filter(
    (banner): banner is BannerOf<P> =>
      fits(banner, place) && !isPaused(banner) && showsOn(banner, page)
  );
};

/**
 * The first banner of a running experiment whose variant the user is in, else
 * the first decided one: a decided winner is what shows by default, so a newer
 * experiment in its place still runs, its control seeing the winner. Reading a
 * flag records an exposure in PostHog, so `variantOf` is asked only about
 * banners that could show here, never about a decided one, and no further
 * once one is found.
 */
export const pickBanner = <B extends DashboardBanner>(
  candidates: B[],
  variantOf: (flagKey: string) => unknown
) =>
  candidates.find(
    ({ experiment }) =>
      !experiment.decided &&
      variantOf(experiment.flagKey) === experiment.variant
  ) ??
  candidates.find(({ experiment }) => experiment.decided) ??
  null;

const fits = (banner: DashboardBanner, place: BannerPlace) => {
  if (place.template === "overlay") return isOverlay(banner);
  if (banner.template === "inline") {
    return place.template === "inline" && banner.slot === place.slot;
  }
  return banner.template === place.template;
};

const isPaused = (banner: DashboardBanner) =>
  isOverlay(banner) && banner.trigger.kind === "off";

const showsOn = ({ showOn, hideOn }: DashboardBanner, pathname: string) =>
  (showOn.length === 0 || showOn.some((rule) => matches(pathname, rule))) &&
  !hideOn.some((rule) => matches(pathname, rule));

/**
 * The page with the plan's id taken off the front: /14545971/proxy/list is
 * /proxy/list for one plan, so an editor's /proxy/list covers every plan's,
 * and /stats covers every plan's stats though no page lives at /stats alone.
 */
const withoutPlanId = (pathname: string) =>
  pathname.replace(/^\/\d+(?=\/|$)/, "") || "/";

/**
 * Starts with covers the path and the pages under it: /proxy covers
 * /proxy/list, not /proxy-tester. Exact covers the path alone, Contains any
 * page whose path holds it. A `*` stands for any characters but a slash.
 */
const matches = (pathname: string, { match, path }: BannerPageRule) => {
  const pattern = path
    .replace(/\/+$/, "")
    .split("*")
    .map((part) => part.replace(/[.+?^${}()|[\]\\]/g, "\\$&"))
    .join("[^/]*");
  if (match === "contains") return new RegExp(pattern).test(pathname);
  const under = match === "startsWith" ? "(/.*)?" : "/?";
  return new RegExp(`^${pattern}${under}$`).test(pathname);
};
